import React, { useEffect, useState } from "react";
import { cn } from "../../lib/utils";

interface AssistantLayoutProps {
  children: React.ReactNode;
  sidebar?: React.ReactNode;
  showSidebar?: boolean;
  onToggleSidebar?: () => void;
}

export const AssistantLayout: React.FC<AssistantLayoutProps> = ({
  children,
  sidebar,
  showSidebar = false,
  onToggleSidebar,
}) => {
  const [scrollY, setScrollY] = useState(0);
  const headerHeight = 64; // 16 * 4px = 64px (h-16 in Tailwind)
  
  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    // Set initial scroll position
    handleScroll();

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Listen for event to collapse sidebar (e.g., when PromptModal opens)
  useEffect(() => {
    const handleCollapseRequest = () => {
      if (showSidebar && onToggleSidebar) {
        onToggleSidebar();
      }
    };

    window.addEventListener("collapseAssistantSidebar", handleCollapseRequest);
    return () => window.removeEventListener("collapseAssistantSidebar", handleCollapseRequest);
  }, [showSidebar, onToggleSidebar]);

  // Calculate dynamic top position - when scrolled past header, stick to top
  const topPosition = Math.max(0, headerHeight - scrollY);

  return (
    <div className="min-h-screen bg-background">
      {/* Main Content - no margin adjustment */}
      <div className="relative">
        {children}
      </div>

      {/* Sidebar panel */}
      <div
        className={cn(
          "fixed right-0 bottom-0 transition-all duration-300 ease-out",
          showSidebar ? "translate-x-0 opacity-100 w-[480px]" : "translate-x-full opacity-0 w-[480px]"
        )}
        style={{ 
          top: `${topPosition}px`,
          zIndex: 9000 // Above Intercom (1000) and most other elements
        }}
      >
        <div className={cn(
          "h-full",
          "bg-gradient-to-r from-background to-background/95 backdrop-blur-sm",
          "border-l border-t border-r border-b border-border/50 rounded-tl-lg rounded-tr-lg rounded-br-lg rounded-bl-lg",
          "shadow-2xl overflow-hidden"
        )}>
          {sidebar}
        </div>
      </div>
    </div>
  );
};